import { API_CONFIG } from '../config/api';
import sessionTimeoutHandler from '../utils/sessionTimeout';

class AdminService {
  constructor() {
    this.baseURL = `${API_CONFIG.baseURL}/admin`;
  }

  // Get auth token from localStorage
  getAuthToken() {
    // Prefer current user token if logged in as admin
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      try {
        const user = JSON.parse(currentUser);
        if (user.isAdmin && user.token) {
          return user.token;
        }
      } catch (error) {
        console.error('Error parsing currentUser from localStorage:', error);
      }
    }

    // Fallback to dedicated admin token
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      return adminToken;
    }

    return null;
  }

  // Set auth headers
  getAuthHeaders() {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Centralized method for authenticated API requests with session timeout handling
  async makeAuthenticatedRequest(url, options = {}) {
    const requestOptions = {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers
      }
    };

    try {
      const response = await fetch(url, requestOptions);

      // Check for session timeout (401/403)
      if (sessionTimeoutHandler.isSessionExpired(response)) {
        // Limpa qualquer sessão (admin ou usuário) e redireciona
        sessionTimeoutHandler.handleSessionTimeout('Sua sessão expirou. Faça login novamente.', true);
        throw new Error('Session expired');
      }

      return response;
    } catch (error) {
      // Re-throw the error for the calling method to handle
      throw error;
    }
  }

  // Helper para processar erros de API de forma padronizada
  async handleApiError(response, defaultMessage) {
    let errorBody = {};
    try { 
      errorBody = await response.json(); 
    } catch (_) {
      // Se não conseguir fazer parse do JSON, usa o status text
    }
    
    let details = '';
    
    // Extrai detalhes de erros (array de strings ou objeto de erros do Mongoose/Sequelize)
    if (Array.isArray(errorBody.errors)) {
      details = errorBody.errors.join('. ');
    } else if (errorBody.errors && typeof errorBody.errors === 'object') {
      details = Object.values(errorBody.errors)
        .map(err => err?.message || String(err))
        .join('. ');
    }
    
    // Se houver detalhes, usa APENAS os detalhes como mensagem principal
    // Se não, traduz mensagens genéricas comuns ou usa a mensagem padrão
    let finalMessage = details;

    if (!finalMessage) {
      const serverMsg = errorBody.message || defaultMessage;
      
      // Traduções simples para mensagens comuns do backend
      if (serverMsg === 'Validation error' || serverMsg === 'Validation error.') {
        finalMessage = 'Erro de validação: Verifique os dados preenchidos.';
      } else if (serverMsg.includes('duplicate key')) {
        finalMessage = 'Registro duplicado: Este item já existe.';
      } else {
        finalMessage = serverMsg;
      }
    }
    
    const error = new Error(finalMessage);
    error.status = response.status;
    error.details = errorBody.errors || null;
    throw error;
  }

  // Admin login
  async login(username, password) {
    try {
      const response = await fetch(`${this.baseURL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        await this.handleApiError(response, 'Login failed');
      }

      const response_data = await response.json();

      // Store token and admin info (compatível com código legado)
      localStorage.setItem('adminToken', response_data.token || response_data.data?.token || response_data.data?.accessToken);
      localStorage.setItem('adminUser', JSON.stringify(response_data.user || response_data.data?.admin || response_data.data?.user));

      const data = response_data.data || {
        admin: response_data.user,
        token: response_data.token
      };

      return data;
    } catch (error) {
      console.error('Admin login error:', error);
      throw error;
    }
  }

  // Admin logout
  logout() {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
  }

  // Get current admin user
  getCurrentAdmin() {
    const adminUser = localStorage.getItem('adminUser');
    if (!adminUser || adminUser === 'undefined' || adminUser === 'null') {
      return null;
    }
    try {
      return JSON.parse(adminUser);
    } catch (error) {
      console.error('Error parsing admin user from localStorage:', error);
      localStorage.removeItem('adminUser');
      return null;
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.getAuthToken();
  }

  // Check if user has specific permission
  hasPermission(permission) {
    const admin = this.getCurrentAdmin();
    return admin && admin.permissions && admin.permissions.includes(permission);
  }

  // Get dashboard stats
  async getDashboardStats() {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.baseURL}/dashboard/stats`);

      if (response.status === 429) {
        // Provide a clearer error for rate limiting
        let errBody = {};
        try { errBody = await response.json(); } catch {}
        throw new Error(errBody.message || 'Rate limit exceeded while fetching dashboard stats');
      }

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard stats');
      }

      const result = await response.json();
      return result.data; // return the actual stats payload
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }

  // Menu management
  async getMenuItems(options = {}) {
    try {
      const {
        page = 1,
        limit = 1000,
        category,
        search,
        sortBy = 'createdAt',
        sortOrder = 'desc',
        unit,
      } = options;

      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
      if (category) params.set('category', category);
      if (search) params.set('search', search);
      if (unit) params.set('unit', unit);

      const response = await this.makeAuthenticatedRequest(`${this.baseURL}/menu?${params.toString()}`);

      if (!response.ok) {
        throw new Error('Failed to fetch menu items');
      }

      const result = await response.json();
      return result.data?.items || [];
    } catch (error) {
      console.error('Error fetching menu items:', error);
      throw error;
    }
  }

  async createMenuItem(menuItem) {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.baseURL}/menu`, {
        method: 'POST',
        body: JSON.stringify(menuItem)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create menu item');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating menu item:', error);
      throw error;
    }
  }

  async updateMenuItem(id, menuItem) {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.baseURL}/menu/${id}`, {
        method: 'PUT',
        body: JSON.stringify(menuItem)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update menu item');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating menu item:', error);
      throw error;
    }
  }

  async deleteMenuItem(id) {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.baseURL}/menu/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete menu item');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting menu item:', error);
      throw error;
    }
  }

  // Bulk delete menu items (client-side batching of DELETE requests)
  async bulkDeleteMenuItems(ids = []) {
    if (!Array.isArray(ids) || ids.length === 0) {
      throw new Error('Nenhum item selecionado para exclusão.');
    }

    const results = {
      successCount: 0,
      failureCount: 0,
      errors: []
    };

    // Execute deletions in parallel with Promise.allSettled
    const deletions = ids.map(id =>
      this.deleteMenuItem(id)
        .then(() => ({ id, ok: true }))
        .catch(err => ({ id, ok: false, error: err.message || 'Erro desconhecido' }))
    );

    const settled = await Promise.allSettled(deletions);
    settled.forEach(s => {
      const res = s.status === 'fulfilled' ? s.value : s.reason;
      if (res && res.ok) {
        results.successCount += 1;
      } else {
        results.failureCount += 1;
        if (res && res.error) results.errors.push({ id: res.id, message: res.error });
      }
    });

    return results;
  }

  // Review management
  async getReviews(status = 'all') {
    try {
      // Check if user is authenticated
      if (!this.isAuthenticated()) {
        throw new Error('Você precisa fazer login como administrador para acessar as avaliações. Use credenciais válidas definidas pelo administrador do sistema.');
      }

      const url = status === 'all' 
        ? `${this.baseURL}/reviews` 
        : `${this.baseURL}/reviews?status=${status}`;
        
      const response = await this.makeAuthenticatedRequest(url);

      if (!response.ok) {
        throw new Error(`Erro ao buscar avaliações: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result.data?.reviews || [];
    } catch (error) {
      console.error('Error fetching reviews:', error);
      throw error;
    }
  }

  async moderateReview(id, action) {
    try {
      const response = await fetch(`${this.baseURL}/reviews/${id}/moderate`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ status: action })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to moderate review');
      }

      return await response.json();
    } catch (error) {
      console.error('Error moderating review:', error);
      throw error;
    }
  }

  // Convenience method: update review status (approve/reject/pending)
  async updateReviewStatus(id, status) {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.baseURL}/reviews/${id}/moderate`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update review status');
      }

      const result = await response.json();
      return result.data?.review || result;
    } catch (error) {
      console.error('Error updating review status:', error);
      throw error;
    }
  }

  // Delete a review
  async deleteReview(id) {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.baseURL}/reviews/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete review');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  }

  // News management

  // News management (repurposed gallery endpoint)
  async getNews() {
    try {
      const response = await fetch(`${this.baseURL}/news`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch news');
      }

      const result = await response.json();
      // Backend returns { data: { images: [...] } } for gallery/news
      return result.data?.images || result.data?.items || [];
    } catch (error) {
      console.error('Error fetching news:', error);
      throw error;
    }
  }

  // Units management
  async getUnits() {
    try {
      // Include inactive units for admin management
      const response = await this.makeAuthenticatedRequest(`${API_CONFIG.baseURL}/units?includeInactive=true`);
      if (!response.ok) {
        await this.handleApiError(response, 'Falha ao buscar unidades');
      }
      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error('Error fetching units:', error);
      throw error;
    }
  }

  async createUnit(unitData) {
    try {
      const response = await this.makeAuthenticatedRequest(`${API_CONFIG.baseURL}/units`, {
        method: 'POST',
        body: JSON.stringify(unitData)
      });
      if (!response.ok) {
        await this.handleApiError(response, 'Falha ao criar unidade');
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating unit:', error);
      throw error;
    }
  }

  async updateUnit(id, unitData) {
    try {
      const response = await this.makeAuthenticatedRequest(`${API_CONFIG.baseURL}/units/${id}`, {
        method: 'PUT',
        body: JSON.stringify(unitData)
      });
      if (!response.ok) {
        await this.handleApiError(response, 'Falha ao atualizar unidade');
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating unit:', error);
      throw error;
    }
  }

  async deactivateUnit(id) {
    return this.updateUnit(id, { isActive: false });
  }

  async deleteUnit(id) {
    try {
      const response = await this.makeAuthenticatedRequest(`${API_CONFIG.baseURL}/units/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        await this.handleApiError(response, 'Falha ao excluir unidade');
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting unit:', error);
      throw error;
    }
  }

  async getUsers(params = {}) {
    try {
      const queryParams = new URLSearchParams(params);
      const response = await fetch(`${this.baseURL}/users?${queryParams}`, {
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  async getUserById(id) {
    try {
      const response = await fetch(`${this.baseURL}/users/${id}`, {
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user');
      }
      
      const result = await response.json();
      return result.data.user;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  async createUser(userData) {
    try {
      const response = await fetch(`${this.baseURL}/users`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) {
        await this.handleApiError(response, 'Failed to create user');
      }
      
      const result = await response.json();
      return result.data.user;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async updateUser(id, userData) {
    try {
      const response = await fetch(`${this.baseURL}/users/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) {
        await this.handleApiError(response, 'Failed to update user');
      }
      
      const result = await response.json();
      return result.data.user;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async resetUserPassword(id, newPassword) {
    try {
      const response = await fetch(`${this.baseURL}/users/${id}/reset-password`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ newPassword })
      });
      
      if (!response.ok) {
        await this.handleApiError(response, 'Failed to reset password');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }

  async deleteUser(id) {
    try {
      const response = await fetch(`${this.baseURL}/users/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        await this.handleApiError(response, 'Failed to delete user');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  async getUserStats() {
    try {
      const response = await fetch(`${this.baseURL}/users/stats/overview`, {
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user statistics');
      }
      
      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
  }


  // News image upload (uses /news/upload)
  async uploadNewsImage(formData) {
    try {
      const token = this.getAuthToken();
      const response = await fetch(`${this.baseURL}/news/upload`, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: formData
      });

      if (!response.ok) {
        await this.handleApiError(response, 'Failed to upload news image');
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading news image:', error);
      throw error;
    }
  }


  async createNews(data) {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.baseURL}/news`, {
        method: 'POST',
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        await this.handleApiError(response, 'Failed to create news');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating news:', error);
      throw error;
    }
  }

  async updateNews(id, data) {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.baseURL}/news/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        await this.handleApiError(response, 'Failed to update news');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating news:', error);
      throw error;
    }
  }

  async deleteNews(id) {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.baseURL}/news/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        await this.handleApiError(response, 'Failed to delete news');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting news:', error);
      throw error;
    }
  }

  // Dishes management
  async getDishes(options = {}) {
    try {
      const {
        page = 1,
        limit = 20,
        category,
        search,
        sortBy = 'createdAt',
        sortOrder = 'desc',
      } = options;

      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
      if (category) params.set('category', category);
      if (search) params.set('search', search);

      const response = await this.makeAuthenticatedRequest(`${this.baseURL}/dishes?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch dishes');
      }
      const result = await response.json();
      return result.data?.items || [];
    } catch (error) {
      console.error('Error fetching dishes:', error);
      throw error;
    }
  }

  async createDish(dish) {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.baseURL}/dishes`, {
        method: 'POST',
        body: JSON.stringify(dish)
      });
      if (!response.ok) {
        await this.handleApiError(response, 'Failed to create dish');
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating dish:', error);
      throw error;
    }
  }

  async updateDish(id, dish) {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.baseURL}/dishes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(dish)
      });
      if (!response.ok) {
        await this.handleApiError(response, 'Failed to update dish');
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating dish:', error);
      throw error;
    }
  }

  async deleteDish(id) {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.baseURL}/dishes/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        await this.handleApiError(response, 'Failed to delete dish');
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting dish:', error);
      throw error;
    }
  }

  // Upload dish image to backend (Cloudinary)
  async uploadDishImage(dishId, file) {
    try {
      const token = this.getAuthToken();
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${this.baseURL}/dishes/${dishId}/image`, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: formData
      });

      if (!response.ok) {
        await this.handleApiError(response, 'Failed to upload dish image');
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading dish image:', error);
      throw error;
    }
  }
}

const adminService = new AdminService();
export default adminService;